/**
 * S-57 台紙 詳細・編集（F-EXP-05）。燃油代・潤滑油代・現像代の 3 区分をカードで表示し、区分ごとに帳票（R-11/12/13）を出力する。
 * 編集可：請求日（必須）・整理番号・支払先・日付・内容・明細備考。
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FileDown, Save, Trash2 } from 'lucide-react';
import { Button, DateField, ErrorState, FormActions, FormField, FormGrid, Loading, PageHeader, Panel, TextField, useConfirm, useToast } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { applyApiErrors } from '@/lib/form';
import { toWareki } from '@/lib/wareki';
import { cx } from '@/lib/cx';
import { Money, Num } from '../common/components';
import { useDeleteMount, useMount, useUpdateMount, type MountCategory, type MountDetail } from './api';

type FormValues = {
  invoiceDate: string;
  fuelRefNo: string;
  fuelPayee: string;
  fuelDate: string;
  lubeRefNo: string;
  lubePayee: string;
  lubeDate: string;
  lubeContent: string;
  devRefNo: string;
  devPayee: string;
  devDate: string;
  devContent: string;
  notes: Record<string, string>;
};

const CATS: { key: MountCategory; prefix: 'fuel' | 'lube' | 'dev'; tone: string; report: string }[] = [
  { key: '燃油代', prefix: 'fuel', tone: 'fuel', report: 'R-11' },
  { key: '潤滑油代', prefix: 'lube', tone: 'lube', report: 'R-12' },
  { key: '現像代', prefix: 'dev', tone: 'dev', report: 'R-13' },
];

export function MountDetailPage() {
  const { id } = useParams();
  const q = useMount(Number(id));
  if (q.isPending) return <Loading block />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return <MountForm key={`${q.data.id}-${q.data.outputFileName}`} m={q.data} />;
}

function MountForm({ m }: { m: MountDetail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { yearPath } = useFiscalYear();
  const update = useUpdateMount(m.id);
  const del = useDeleteMount();
  const listPath = yearPath('forms/receipt-mounts');
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      invoiceDate: m.invoiceDate ?? '',
      fuelRefNo: m.fuelRefNo ?? '',
      fuelPayee: m.fuelPayee ?? '',
      fuelDate: m.fuelDate ?? '',
      lubeRefNo: m.lubeRefNo ?? '',
      lubePayee: m.lubePayee ?? '',
      lubeDate: m.lubeDate ?? '',
      lubeContent: m.lubeContent ?? '',
      devRefNo: m.devRefNo ?? '',
      devPayee: m.devPayee ?? '',
      devDate: m.devDate ?? '',
      devContent: m.devContent ?? '',
      notes: Object.fromEntries(m.lines.map((l) => [String(l.id), l.note ?? ''])),
    },
  });

  const onSave = handleSubmit(async (v) => {
    if (!v.invoiceDate) {
      setError('invoiceDate', { message: '請求日は必須です' });
      return;
    }
    const n = (s: string) => (s.trim() ? s.trim() : null);
    try {
      await update.mutateAsync({
        invoiceDate: v.invoiceDate,
        fuelRefNo: n(v.fuelRefNo),
        fuelPayee: n(v.fuelPayee),
        fuelDate: n(v.fuelDate),
        lubeRefNo: n(v.lubeRefNo),
        lubePayee: n(v.lubePayee),
        lubeDate: n(v.lubeDate),
        lubeContent: n(v.lubeContent),
        devRefNo: n(v.devRefNo),
        devPayee: n(v.devPayee),
        devDate: n(v.devDate),
        devContent: n(v.devContent),
        lines: m.lines.map((l) => ({ id: l.id, note: n(v.notes[String(l.id)] ?? '') })),
      });
      toast.success('台紙を更新しました');
    } catch (e) {
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });

  const onDownload = async (category: MountCategory) => {
    if (isDirty && !(await confirm({ title: '未保存の変更があります', message: '保存前の内容で帳票を出力します。よろしいですか？', confirmLabel: '出力する' }))) return;
    try {
      const name = await downloadFile(`/receipt-mounts/${m.id}/report.xlsx`, { query: { category } });
      toast.success('帳票を出力しました', name);
    } catch (e) {
      toast.error('帳票を出力できませんでした', errorMessage(e));
    }
  };

  const onDelete = async () => {
    if (!(await confirm({ title: '削除しますか？', message: 'この台紙を削除します。この操作は取り消せません。', tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await del.mutateAsync(m.id);
      toast.success('削除しました');
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const values = {
    fuel: { unit: m.fuelUnitPrice, qty: m.fuelQuantity, excl: m.fuelAmountExclTax, tax: m.fuelTax as number | null, total: m.fuelTotal, content: m.fuelContent },
    lube: { unit: m.lubeUnitPrice, qty: m.lubeQuantity, excl: m.lubeAmountExclTax, tax: null, total: m.lubeTotal, content: m.lubeContent },
    dev: { unit: m.devUnitPrice, qty: m.devQuantity, excl: m.devAmountExclTax, tax: m.devTax as number | null, total: m.devTotal, content: m.devContent },
  };

  return (
    <form onSubmit={onSave}>
      <PageHeader
        eyebrow="台紙作成"
        title={m.vesselName}
        description={`調査期間 ${m.periodFrom}〜${m.periodTo}　名簿No ${m.rosterNo}　所属組合 ${m.businessOperatorName}`}
        meta={m.outputFileName ? <span className="bs-badge bs-badge--navy bs-num">{m.outputFileName}</span> : undefined}
      />
      <div className="bs-exp-stack">
        <Panel title="請求情報">
          <FormGrid columns={3}>
            <FormField label="請求日" required error={errors.invoiceDate?.message} hint={watch('invoiceDate') ? toWareki(watch('invoiceDate')) : '初期値は給油日'}>
              <DateField {...register('invoiceDate')} />
            </FormField>
            <FormField label="登録番号（インボイス）">
              <TextField value={m.invoiceRegistrationNo ? `T${m.invoiceRegistrationNo}` : ''} readOnly />
            </FormField>
            <FormField label="宛先（請求書兼領収書欄）">
              <TextField value={m.claimantName ?? ''} readOnly />
            </FormField>
          </FormGrid>
        </Panel>

        <div className="bs-exp-mounts">
          {CATS.map((c) => {
            const v = values[c.prefix];
            const lines = m.lines.filter((l) => l.category === c.key);
            return (
              <section key={c.key} className={cx('bs-exp-mount', `bs-exp-mount--${c.tone}`)} aria-label={`${c.key}の台紙`}>
                <header className="bs-exp-mount__head">
                  <span className="bs-exp-mount__title">{c.key}</span>
                  <Button size="sm" icon={<FileDown size={14} />} onClick={() => void onDownload(c.key)}>
                    帳票出力（{c.key}）
                  </Button>
                </header>
                <div className="bs-exp-mount__body">
                  <FormField label="整理番号">
                    <TextField {...register(`${c.prefix}RefNo` as const)} />
                  </FormField>
                  <FormField label="支払先">
                    <TextField {...register(`${c.prefix}Payee` as const)} />
                  </FormField>
                  <FormField label="日付">
                    <DateField {...register(`${c.prefix}Date` as const)} />
                  </FormField>
                  {c.prefix !== 'fuel' && (
                    <FormField label="内容">
                      <TextField {...register(`${c.prefix}Content` as 'lubeContent' | 'devContent')} />
                    </FormField>
                  )}
                  <dl className="bs-exp-mount__kv">
                    {c.prefix === 'fuel' && (
                      <>
                        <dt>内容</dt>
                        <dd>{v.content}</dd>
                      </>
                    )}
                    <dt>単価</dt>
                    <dd className="is-right"><Num value={v.unit} /></dd>
                    <dt>数量</dt>
                    <dd className="is-right"><Num value={v.qty} digits={3} /></dd>
                    <dt>金額(税抜)</dt>
                    <dd><Money value={v.excl} unit="円" /></dd>
                    <dt>消費税</dt>
                    <dd>{v.tax == null ? <span className="bs-exp-muted">空白</span> : <Money value={v.tax} unit="円" />}</dd>
                    <dt>合計金額</dt>
                    <dd><strong><Money value={v.total} unit="円" /></strong></dd>
                  </dl>
                  <div className="bs-exp-sheet">
                    <table aria-label={`${c.key} 明細`}>
                      <thead>
                        <tr>
                          <th>品名</th>
                          <th>金額</th>
                          <th>備考</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l) => (
                          <tr key={l.id}>
                            <td>{l.itemName}</td>
                            <td className="is-right"><Money value={l.amount} /></td>
                            <td>
                              <input className="bs-exp-cell-input is-text" style={{ width: '100%' }} aria-label={`${c.key} ${l.itemName} 備考`} {...register(`notes.${l.id}` as const)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <FormActions>
        <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => void onDelete()} loading={del.isPending}>
          削除
        </Button>
        <span style={{ flex: 1 }} />
        <Button onClick={() => navigate(listPath)}>一覧へ戻る</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting || update.isPending} disabled={!isDirty}>
          保存する
        </Button>
      </FormActions>
    </form>
  );
}
